import i18n, { BackendModule, ThirdPartyModule } from 'i18next';
import { I18nextMergeBackend, I18nextMergeBackendOptions } from './I18nextMergeBackend';
import { FallbackChainModule } from './FallbackChainModule';
import { NovaInterpolator } from './NovaInterpolator';
import { normalizeLocaleString } from './localeUtils';

let novaLanguage = new URLSearchParams(window.location.search)
  .get('nova-language')
  ?.replace('_', '-') // i18next doesn't seem to recognize _ as a separator.
  ?.toLowerCase();
if (novaLanguage === 'zh-xx') {
  novaLanguage = 'zh-CN-pseudo';
}
if (!novaLanguage) {
  novaLanguage = navigator.language;
}

export const configuredLanguage = normalizeLocaleString(novaLanguage);

/**
 * Initialize i18next with a backend. Needs to be called once during application startup.
 * @param backends An array of i18next backends to be used with the chained backend.
 * @param backendOptions An i18next options array for the backends
 * @param initialLanguage A BCP-47 locale that will be used as the initial language. This will override any user preferences.
 */
export function initI18next({
  backends,
  backendOptions,
  initialLanguage,
  ...config
}: {
  backends: Array<BackendModule>;
  backendOptions?: Array<object>;
  initialLanguage?: string;
  modules: ThirdPartyModule[];
}) {
  const isDevMode = process.env.NODE_ENV === 'development';
  const mergeBackendOptions: I18nextMergeBackendOptions = {
    backends: backends.map((backend, i) => {
      return {
        instance: backend,
        options: backendOptions?.[i] ?? null,
      };
    }),
  };

  if (isDevMode) {
    // This would be simpler to use Array.sort but we can't guarantee sort is stable
    // in all of our supported browsers.
    const highPriorityBackends = mergeBackendOptions.backends.filter(
      (b) => (b.instance as any).devPriority !== 'low'
    );
    const lowPriorityBackends = mergeBackendOptions.backends.filter(
      (b) => (b.instance as any).devPriority === 'low'
    );
    mergeBackendOptions.backends = [...highPriorityBackends, ...lowPriorityBackends];
  }

  const defaultModules = [
    new I18nextMergeBackend(),
    new FallbackChainModule(),
    new NovaInterpolator(),
  ];

  return [...defaultModules, ...config.modules]
    .reduce((i18n, module) => i18n.use(module), i18n)
    .init({
      debug: false,
      fallbackLng: 'en',
      lng: initialLanguage ?? configuredLanguage,
      keySeparator: false,
      interpolation: {
        prefix: '{',
        suffix: '}',
        escapeValue: false,
      },
      backend: mergeBackendOptions,
    });
}

let initialized = false;
i18n.on('initialized', () => {
  initialized = true;
});

export const waitForInit = async () => {
  if (initialized) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    i18n.on('initialized', resolve);
  });
};

/**
 * Load a message bundle manually. The main use case is to start loading the bundle before it's
 * needed.
 *
 * @param bundleName Name of the bundle to load
 * @returns a Promise that resolves when loading is complete
 */
export const loadBundle = async (bundleName: string) => {
  await waitForInit();
  const loaded = i18n.hasResourceBundle(i18n.language, bundleName);
  if (!loaded) {
    await i18n.loadNamespaces(bundleName);
  }
};

export interface MessageParams {
  [key: string]: string;
}

export type Localizer = (key: string, params?: MessageParams) => string;

/**
 * Returns a Promise that resolves to Localizer for the passed bundle. Will load the bundle if it is not already loaded.
 * @param bundleName Name of the bundle to get.
 * @returns a Promise that resolves to a Localizer, a function that will provide a localized message for a given key, optional parameter object, and for the passed bundle.
 */
export const getTextLocalizer = async (bundleName: string): Promise<Localizer> => {
  await loadBundle(bundleName);
  return (key, params) => i18n.t(`${bundleName}:${bundleName}.${key}`, params);
};

/**
 * Returns a function that will provide localized message for a given key and option parameter object.
 * @param bundleName Name of the bundle to get.
 * @deprecated Use getTextLocalizer instead.
 */
export const _getTextLocalizer = (bundleName: string): Localizer => (key, params) => {
  return i18n.t(`${bundleName}:${bundleName}.${key}`, params);
};

/**
 * Imperatively sets the language of the application.
 * @param language The new language
 */
export const setLanguage = (language: string) => {
  i18n.changeLanguage(language);
};

/**
 * Imperatively sets a handler for when the language changes.
 * @param onChange Handler function to be called when the language is changed.
 * @returns A function to call to stop listening for language change events.
 */
export const onLanguageChange = (onChange: (language: string) => void) => {
  i18n.on('languageChanged', onChange);
  return () => {
    i18n.off('languageChanged', onChange);
  };
};

export const getLanguage = () => i18n.language ?? configuredLanguage;
